import { useEffect, useCallback, useRef } from "react"
const useExitIntent = ({
  onExitIntent,
  threshold = 10,
  delay = 100,
  enabled = true,
  sessionKey = "exit_intent_shown",
}) => {
  const hasShownRef = useRef(false)
  const timeoutRef = useRef(null)
  const mousePositionRef = useRef({ x: 0, y: 0 })

  const hasShownInSession = useCallback(() => {
    return sessionStorage.getItem(sessionKey) === "true"
  }, [sessionKey])

  const markAsShown = useCallback(() => {
    sessionStorage.setItem(sessionKey, "true")
    hasShownRef.current = true
  }, [sessionKey])

  const handleMouseMove = useCallback(
    (e) => {
      if (!enabled || hasShownRef.current || hasShownInSession()) {
        return
      }

      const { clientY } = e
      mousePositionRef.current = { x: e.clientX, y: clientY }

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }

      if (clientY <= threshold) {
        timeoutRef.current = setTimeout(() => {
          if (onExitIntent && typeof onExitIntent === "function") {
            onExitIntent()
            markAsShown()
          }
        }, delay)
      }
    },
    [enabled, threshold, delay, onExitIntent, markAsShown, hasShownInSession],
  )

  const handleBeforeUnload = useCallback(
    (e) => {
      if (!enabled || hasShownRef.current || hasShownInSession()) {
        return
      }

      onExitIntent()
      markAsShown()
    },
    [enabled, onExitIntent, markAsShown, hasShownInSession],
  )

  const handlePageUnload = useCallback(() => {
    if (!enabled || hasShownRef.current || hasShownInSession()) {
      return
    }

    onExitIntent()
    markAsShown()
  }, [enabled, onExitIntent, markAsShown, hasShownInSession])

  const handleVisibilityChange = useCallback(() => {
    if (!enabled || hasShownRef.current || hasShownInSession()) {
      return
    }

    if (document.hidden) {
      onExitIntent()
      markAsShown()
    }
  }, [enabled, onExitIntent, markAsShown, hasShownInSession])

  useEffect(() => {
    if (!enabled) {
      return
    }

    document.addEventListener("mousemove", handleMouseMove, { passive: true })
    window.addEventListener("beforeunload", handleBeforeUnload)
    window.addEventListener("unload", handlePageUnload)
    document.addEventListener("visibilitychange", handleVisibilityChange)

    return () => {
      document.removeEventListener("mousemove", handleMouseMove)
      window.removeEventListener("beforeunload", handleBeforeUnload)
      window.removeEventListener("unload", handlePageUnload)
      document.removeEventListener("visibilitychange", handleVisibilityChange)

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [enabled, handleMouseMove, handleBeforeUnload, handlePageUnload, handleVisibilityChange])

  const reset = useCallback(() => {
    sessionStorage.removeItem(sessionKey)
    hasShownRef.current = false
  }, [sessionKey])

  return {
    hasShown: hasShownRef.current || hasShownInSession(),
    reset,
  }
}

export default useExitIntent
